import React, { useMemo } from "react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { TeacherTestDetails } from "p6m-p6u";
import PDFFullTest from "../../../complex/pdfCreation/PDFFullTest";
import { useLogger } from "../../../hooks/useLogger";
import { useT } from "@transifex/react";
import { ensureReliableOverallScore } from "../../../helpers/ScoringHelper";
import usePDFUnits from "../../../hooks/usePDFUnits";
import useBookData from "../../../hooks/useBookData";
import { LINK_BUTTON_LOOKALIKE_CLASSES } from "../../LinkButton";

import {
    PDFDownloadInnerButton,
    PDFDownloadButtonContainer, //necessary to remove the link styling
} from "./StyledComponents";

export enum PDFDownloadButtonVariants {
    BUTTON = "BUTTON",
    TEXT_LINK = "TEXT_LINK",
}

interface PDFDownloadButtonProps {
    buttonText: string;
    fileName: string;
    testDetails?: TeacherTestDetails;
    variant?: PDFDownloadButtonVariants;
    secondary?: boolean;
    isSolution?: boolean;
    fullWidth?: boolean;
    /** Logged as `source` on the download event to tell the download spots apart. */
    analyticsSource: string;
    onDownloadClick?: () => void;
}

const PDFDownloadButton: React.FC<PDFDownloadButtonProps> = (props) => {
    const { logEvent } = useLogger();
    const variant = props.variant ?? PDFDownloadButtonVariants.BUTTON;
    const { pdfUnits } = usePDFUnits(props.testDetails);

    const t = useT();
    const t_loading = t("Loading", { _tags: "CreateTest,PDFCreation" });

    const cleanedUpTestDetails = useMemo(
        () => ensureReliableOverallScore(props.testDetails ?? {}),
        [props.testDetails]
    );

    const { bookData } = useBookData(cleanedUpTestDetails?.articleId);

    const handleClick = () => {
        logEvent("Vokabeltest v2 Downloaded", {
            testId: cleanedUpTestDetails?.id ?? "",
            isSolution: props.isSolution,
            source: props.analyticsSource,
        });
        props.onDownloadClick?.();
    };

    const renderContent = (loading: boolean) => {
        const text = loading ? t_loading : props.buttonText;

        if (variant === PDFDownloadButtonVariants.TEXT_LINK) {
            return (
                <span
                    data-testid="button-download-pdf"
                    className={LINK_BUTTON_LOOKALIKE_CLASSES}
                >
                    {text}
                </span>
            );
        }

        return (
            <PDFDownloadInnerButton
                data-testid="button-download-pdf"
                icon={"download"}
                iconPosition={"RIGHT"}
                buttonStyle={props.secondary ? "BLANK" : undefined}
                fullWidth={props.fullWidth}
            >
                {text}
            </PDFDownloadInnerButton>
        );
    };

    return (
        <PDFDownloadButtonContainer fullWidth={props.fullWidth}>
            <PDFDownloadLink
                document={
                    <PDFFullTest
                        testDetails={cleanedUpTestDetails}
                        bookArticleId={cleanedUpTestDetails?.articleId}
                        pdfUnits={pdfUnits}
                        bookData={bookData}
                        isSolution={props.isSolution}
                    />
                }
                fileName={props.fileName}
                onClick={handleClick}
            >
                {({ loading }) => renderContent(loading)}
            </PDFDownloadLink>
        </PDFDownloadButtonContainer>
    );
};

export default PDFDownloadButton;
